import type { Ref } from 'vue';
import type { useQuoteGeneration } from '@/composables/useQuoteGeneration';
import type { useQuoteStore } from '@/stores/quoteStore';
import type { CotizaTodasResponse } from '@/types/quote.types';
import { trackWidgetEvent } from '@/utils/widget-analytics';
import {
  buildCrmPersonalData,
  buildGatePersonalData,
  buildQuickPersonalData,
  fetchInsurersAndChubb,
  hasAvailableQuotes,
  resolveAuthenticGender,
  storeCotizacionIds,
} from './quoteHelpers';
import type { DriverFormState } from './useDriverForm';

type QuoteGeneration = ReturnType<typeof useQuoteGeneration>;
type QuoteStore = ReturnType<typeof useQuoteStore>;

export interface QuoteFlowDeps {
  /** Reactive subset of the component props the flow reads. */
  props: { xcf?: boolean };
  quoteStore: QuoteStore;
  quoteGeneration: QuoteGeneration;
  formData: DriverFormState;
  chubbEnabled: Ref<boolean>;
  showPersonalData: Ref<boolean>;
  showResults: Ref<boolean>;
  demographicsOnlyGate: Ref<boolean>;
}

const openDriverGate = (deps: QuoteFlowDeps, demographicsOnly: boolean): void => {
  deps.quoteStore.setError(null);
  deps.demographicsOnlyGate.value = demographicsOnly;
  deps.showPersonalData.value = true;
  deps.showResults.value = false;
};

const quickQuote = async (deps: QuoteFlowDeps): Promise<void> => {
  const { quoteStore, quoteGeneration, formData, showResults, showPersonalData } = deps;
  quoteStore.setError(null);
  deps.demographicsOnlyGate.value = false;
  quoteStore.setPersonalData(buildQuickPersonalData(formData.postalCode));

  try {
    const cotizacion = await quoteGeneration.generateQuote(quoteStore.quoteData, false, false);

    if (cotizacion && cotizacion.Id_Cotizacion) {
      storeCotizacionIds(quoteStore, cotizacion);
      await fetchInsurersAndChubb(quoteStore, quoteGeneration, cotizacion.Id_Cotizacion);

      trackWidgetEvent('session_start', {});
      showResults.value = true;
      showPersonalData.value = false;
    }
  } catch (error) {
    quoteStore.setError(error instanceof Error ? error.message : 'Error al generar la cotización');
  }
};

const createContinueHandler = (deps: QuoteFlowDeps) => async (): Promise<void> => {
  if (!deps.quoteStore.quoteData.model) return;

  // Con xcf: gate de contacto antes de cotizar (sin Insert todavía)
  if (deps.props.xcf === true) return openDriverGate(deps, false);
  // Chubb activo: pedir género + nacimiento auténticos antes de cotizar
  if (deps.chubbEnabled.value) return openDriverGate(deps, true);

  await quickQuote(deps);
};

/** Stores the driver data; returns false (with an error set) when the gate lacks demographics. */
const storeDriverPersonalData = (deps: QuoteFlowDeps): boolean => {
  const { quoteStore, formData } = deps;
  const authenticGender = resolveAuthenticGender(formData);

  // Demographics-only gate: keep Q/ANA technical fallbacks only when Chubb demos missing
  // (should not happen — form blocks submit). Never invent demos for Chubb path.
  if (!deps.demographicsOnlyGate.value) {
    quoteStore.setPersonalData(buildCrmPersonalData(formData, authenticGender, deps.chubbEnabled.value));
    return true;
  }
  if (!authenticGender) {
    quoteStore.setError('Indica género y fecha de nacimiento para continuar.');
    return false;
  }
  quoteStore.setPersonalData(buildGatePersonalData(formData, authenticGender));
  return true;
};

const showQuoteOutcome = (deps: QuoteFlowDeps): void => {
  const { quoteGeneration, showResults, showPersonalData } = deps;
  if (hasAvailableQuotes(quoteGeneration) || quoteGeneration.chubbQuotes.value?.Cobertura) {
    trackWidgetEvent('session_start', {});
    showResults.value = true;
    showPersonalData.value = false;
    deps.demographicsOnlyGate.value = false;
  } else {
    showResults.value = false;
    showPersonalData.value = true;
  }
};

const submitDriverQuote = async (deps: QuoteFlowDeps, hasExistingCotizacion: boolean): Promise<void> => {
  const { quoteStore, quoteGeneration } = deps;
  try {
    const cotizacion = await quoteGeneration.generateQuote(quoteStore.quoteData, hasExistingCotizacion, false);

    if (cotizacion && cotizacion.Id_Cotizacion) {
      storeCotizacionIds(quoteStore, cotizacion);
      await fetchInsurersAndChubb(quoteStore, quoteGeneration, cotizacion.Id_Cotizacion);
      showQuoteOutcome(deps);
    }
  } catch (error) {
    quoteStore.setError(error instanceof Error ? error.message : 'Error al actualizar la cotización');
  } finally {
    quoteStore.setLoading(false);
  }
};

const createGetQuoteHandler = (deps: QuoteFlowDeps) => async (): Promise<void> => {
  const { quoteStore } = deps;
  quoteStore.setLoading(true);
  quoteStore.setError(null);

  const hasExistingCotizacion = Boolean(quoteStore.quoteData.cotizacionData?.idCotizacion);
  if (!storeDriverPersonalData(deps)) {
    quoteStore.setLoading(false);
    return;
  }
  await submitDriverQuote(deps, hasExistingCotizacion);
};

/** Step navigation handlers (back to vehicle, edit vehicle / driver from results). */
const createNavigationHandlers = (deps: QuoteFlowDeps, fillFromStore: () => void) => {
  const handleChangeCar = (): void => {
    deps.showPersonalData.value = false;
    deps.showResults.value = false;
    deps.demographicsOnlyGate.value = false;
  };

  const handleEditDriverFromResults = (): void => {
    deps.showResults.value = false;
    deps.showPersonalData.value = true;
    deps.demographicsOnlyGate.value = deps.chubbEnabled.value && deps.props.xcf !== true;
    fillFromStore();
  };

  const handleQuotesUpdated = (response: CotizaTodasResponse): void => {
    deps.quoteGeneration.quotesResponse.value = response;
  };

  return {
    handleChangeCar,
    handleEditVehicleFromResults: handleChangeCar,
    handleEditDriverFromResults,
    handleQuotesUpdated,
  };
};

/** Continue / submit handlers plus navigation between the widget steps. */
export const useQuoteFlow = (deps: QuoteFlowDeps, fillFromStore: () => void) => ({
  handleContinue: createContinueHandler(deps),
  handleGetQuote: createGetQuoteHandler(deps),
  ...createNavigationHandlers(deps, fillFromStore),
});
